import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Schedule } from '@mui/icons-material';

/**
 * One field in the sync-run detail panel. Overline and caption render as
 * spans, so a label and an inline value share a line ("TRIGGERManual",
 * "RUN ID…"). A column with a block label puts the label above the value
 * for every run kind that uses this panel.
 */
export const RunDetailField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, maxWidth: '100%' }}>
    <Typography variant="overline" component="div" color="text.disabled" sx={{ display: 'block', lineHeight: 1.6 }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>{children}</Box>
  </Box>
);

/**
 * Trigger word, and the short starter label under it when the run recorded one.
 * `row` is the collapsed Recent Runs line. `detail` is the expanded panel value.
 */
export const SyncRunTriggerBlock: React.FC<{
  triggerLabel: string;
  actorLabel: string | null;
  hint: string;
  scheduled?: boolean;
  size: 'row' | 'detail';
}> = ({ triggerLabel, actorLabel, hint, scheduled, size }) => {
  const detail = size === 'detail';
  return (
    <Tooltip title={hint}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          minWidth: 0,
          maxWidth: '100%',
          mt: detail ? 0 : 0.4,
          color: 'inherit',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.35,
            fontSize: detail ? '0.8rem' : '0.65rem',
            fontWeight: 600,
            lineHeight: detail ? 1.4 : 1.3,
          }}
        >
          {scheduled && <Schedule sx={{ fontSize: detail ? 14 : 11 }} />}
          {triggerLabel}
        </Box>
        {actorLabel && (
          <Typography
            component="div"
            sx={{
              fontSize: detail ? '0.8rem' : '0.65rem',
              fontWeight: 600,
              lineHeight: 1.3,
              mt: 0.25,
              maxWidth: '100%',
              overflowWrap: 'anywhere',
              color: 'text.secondary',
            }}
          >
            {actorLabel}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};
